import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { AlertCircle, CheckCircle2, Mail } from 'lucide-react';

export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-extrabold text-indigo-600">RaffleBowl</Link>
        </div>

        <Card padding="lg">
          {submitted ? (
            <div className="text-center py-4">
              <div className="bg-emerald-100 dark:bg-emerald-900 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Check Your Email</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                We've sent a password reset link to <span className="font-medium text-gray-700 dark:text-gray-300">{email}</span>. Check your inbox and follow the instructions.
              </p>
              <Link to="/login">
                <Button fullWidth className="py-3 text-base">Back to Sign In</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="bg-indigo-100 dark:bg-indigo-900 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Forgot Password?</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">No worries. Enter your email and we'll send you a reset link.</p>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mb-5">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <Input label="Email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} placeholder="your@email.com" required />
                <Button type="submit" fullWidth isLoading={isLoading} className="py-3 text-base">Send Reset Link</Button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Remember your password?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-semibold">Sign in</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
